import {
	type EditorState as CodeMirrorEditorState,
	Transaction as CodeMirrorTransaction,
} from "@codemirror/state";
import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";
import {
	type EditorState as ProseMirrorEditorState,
	Transaction as ProseMirrorTransaction,
} from "prosemirror-state";

import { safeStringify, serializePage } from "@moment/api-collab";
import { Logger } from "@moment/logging";

import { $documentLocalPath } from "../document-list";
import { env } from "../env";
import { fs } from "../filesystem";
import { paths } from "../paths";
import { $pageInitialEditorState } from "./page-editor.atom";
import { $pageMetadata } from "./page.atom";

const logger = new Logger("page-editor-state");

// ============================================================================
// Editor State Atoms
// ============================================================================

/**
 * Private atom to hold the current editor state value
 * Starts as undefined, gets set when transactions are applied
 */
const $editorStateValue = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const valueAtom = atom(undefined as ProseMirrorEditorState | undefined);
		valueAtom.debugLabel = `editorStateValue/${documentId}/${pageId}`;

		return valueAtom;
	},
	isEqual
);

/**
 * Holds the current ProseMirror EditorState for a page
 * Initializes from $pageInitialEditorState on first read, then becomes writable
 * Draft/collab state is handled by the collab library, not here
 */
export const $pageEditorState = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const editorStateAtom = atom(
			async (get) => {
				// Check if we have a value from transactions
				const value = get($editorStateValue({ documentId, pageId }));
				if (value !== undefined) {
					return value;
				}

				// Otherwise, initialize from $pageInitialEditorState
				return await get($pageInitialEditorState({ documentId, pageId }));
			},
			(_get, set, newState: ProseMirrorEditorState | undefined) => {
				// Update the stored value
				set($editorStateValue({ documentId, pageId }), newState);
			}
		);
		editorStateAtom.debugLabel = `pageEditorState/${documentId}/${pageId}`;

		return editorStateAtom;
	},
	isEqual
);

/**
 * Holds the current CodeMirror EditorState for markdown editing
 * Components should initialize this explicitly
 */
export const $pageMarkdownEditorState = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const editorStateAtom = atom(undefined as CodeMirrorEditorState | undefined);
		editorStateAtom.debugLabel = `pageMarkdownEditorState/${documentId}/${pageId}`;

		return editorStateAtom;
	},
	isEqual
);

// ============================================================================
// Transaction Application
// ============================================================================

/**
 * Writable atom that applies a transaction to the editor state
 * Automatically triggers debounced persistence and compilation
 */
export const $applyPageTransaction = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(
			null, // no read value
			async (get, set, transaction: ProseMirrorTransaction | CodeMirrorTransaction) => {
				if (transaction instanceof ProseMirrorTransaction) {
					const current = await get($pageEditorState({ documentId, pageId }));
					if (!current) {
						logger.error("applyTransaction: No editor state found", {
							documentId,
							pageId,
						});
						throw new Error("No editor state");
					}

					// DEBUG: Log transaction state mismatch before it causes an error
					const transactionBefore = transaction.before;
					if (transactionBefore !== current.doc) {
						logger.error("applyTransaction: Transaction state mismatch detected", {
							documentId,
							pageId,
							docData: safeStringify({
								currentDocFirstChild: current.doc.firstChild?.type.name,
								currentDocSize: current.doc.content.size,
								transactionBeforeFirstChild:
									transactionBefore.firstChild?.type.name,
								transactionBeforeSize: transactionBefore.content.size,
								transactionStepsCount: transaction.steps.length,
								transactionDocChanged: transaction.docChanged,
							}),
						});
					}

					// Apply transaction
					let updated: ProseMirrorEditorState;
					try {
						updated = current.apply(transaction);
					} catch (err) {
						logger.error("applyTransaction: Failed to apply transaction", {
							documentId,
							pageId,
							error: err instanceof Error ? err.message : String(err),
							currentDocFirstChild: current.doc.firstChild?.type.name,
							currentDocSize: current.doc.content.size,
							transactionBeforeFirstChild: transactionBefore.firstChild?.type.name,
							transactionBeforeSize: transactionBefore.content.size,
							transactionStepsCount: transaction.steps.length,
							transactionDocChanged: transaction.docChanged,
						});
						throw err;
					}

					// Update the page editor state atom
					set($pageEditorState({ documentId, pageId }), updated);

					// Trigger debounced persistence
					set($triggerPersistence({ documentId, pageId }), Date.now());

					// Trigger debounced compilation
					set($triggerCompilation({ documentId, pageId }), Date.now());
				} else if (transaction instanceof CodeMirrorTransaction) {
					// Handle CodeMirror transactions
					set($pageMarkdownEditorState({ documentId, pageId }), transaction.state);

					// Trigger debounced persistence
					set($triggerPersistence({ documentId, pageId }), Date.now());
				}
			}
		),
	isEqual
);

// ============================================================================
// Persistence Atoms
// ============================================================================

/**
 * Tracks the last timestamp when persistence was triggered
 * Used for debouncing
 */
const $persistenceTimestamp = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const timestampAtom = atom(0);
		timestampAtom.debugLabel = `persistenceTimestamp/${documentId}/${pageId}`;
		return timestampAtom;
	},
	isEqual
);

/**
 * Writable atom that triggers persistence with debouncing
 * Only persists after 2 seconds of no updates
 */
export const $triggerPersistence = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(
			(get) => get($persistenceTimestamp({ documentId, pageId })),
			(get, set, timestamp: number) => {
				set($persistenceTimestamp({ documentId, pageId }), timestamp);

				// Debounce: only persist after 2 seconds of no updates
				setTimeout(async () => {
					const current = get($persistenceTimestamp({ documentId, pageId }));
					if (current === timestamp) {
						// No updates in 2s, persist now
						try {
							await set($savePageToDisk({ documentId, pageId }));
						} catch (error) {
							logger.error("Error persisting page", { documentId, pageId, error });
						}
					}
				}, 2000);
			}
		),
	isEqual
);

/**
 * Writable atom that saves the current editor state to disk
 */
export const $savePageToDisk = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(null, async (get, set) => {
			if (!env.isDesktopApp) {
				logger.debug("Not saving to disk: not in desktop app", { documentId, pageId });
				return;
			}

			const editorState = await get($pageEditorState({ documentId, pageId }));
			if (!editorState) {
				logger.debug("Not saving to disk: no editor state", { documentId, pageId });
				return;
			}

			const documentPathLoadable = get(loadable($documentLocalPath(documentId)));
			const documentPath =
				documentPathLoadable.state === "hasData" ? documentPathLoadable.data : undefined;
			if (!documentPath) {
				logger.debug("Not saving to disk: no document path", { documentId, pageId });
				return;
			}

			// Serialize ProseMirror doc to markdown and write to disk
			const pathsApi = paths();
			const fsApi = fs();

			// Get page metadata to check for explicit path
			const pageMetadata = await get($pageMetadata({ documentId, pageId }));
			const pagePath = pageMetadata?.path;

			// Only create page directory if using default path structure (not imported documents)
			if (!pagePath) {
				const pageDirPath = await get(pathsApi.getPageDirPath({ documentId, pageId }));
				await set(fsApi.mkdir, { dirPath: pageDirPath, options: { recursive: true } });
			}

			// Write page content - serialize the ProseMirror doc to markdown
			const pageFilePath = await get(
				pathsApi.getPageFilePath({ documentId, pageId, pagePath })
			);

			await set(fsApi.writeTextFile, {
				filePath: pageFilePath,
				content: serializePage(editorState.doc),
				documentId,
			});

			logger.debug("Saved page to disk", { documentId, pageId, documentPath });
		}),
	isEqual
);

// ============================================================================
// Compilation Atoms
// ============================================================================

/**
 * Type for compiled cell information
 */
export type CellMap = Record<string, any>;

/**
 * Holds the compiled cells for a page
 */
export const $compiledCells = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const cellsAtom = atom({} as CellMap);
		cellsAtom.debugLabel = `compiledCells/${documentId}/${pageId}`;
		return cellsAtom;
	},
	isEqual
);

/**
 * Tracks the last timestamp when compilation was triggered
 * Used for debouncing
 */
const $compilationTimestamp = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) => {
		const timestampAtom = atom(0);
		timestampAtom.debugLabel = `compilationTimestamp/${documentId}/${pageId}`;
		return timestampAtom;
	},
	isEqual
);

/**
 * Writable atom that triggers compilation with debouncing
 * Only compiles after 2 seconds of no updates
 */
export const $triggerCompilation = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(
			(get) => get($compilationTimestamp({ documentId, pageId })),
			(get, set, timestamp: number) => {
				set($compilationTimestamp({ documentId, pageId }), timestamp);

				// Debounce: only compile after 2 seconds of no updates
				setTimeout(async () => {
					const current = get($compilationTimestamp({ documentId, pageId }));
					if (current === timestamp) {
						// No updates in 2s, compile now
						try {
							await set($compilePage({ documentId, pageId }));
						} catch (error) {
							logger.error("Error compiling page", { documentId, pageId, error });
						}
					}
				}, 2000);
			}
		),
	isEqual
);

/**
 * Writable atom that compiles the cells in the current page
 * Updates the $compiledCells atom with the results
 */
export const $compilePage = atomFamily(
	({ documentId, pageId }: { documentId: string; pageId: string }) =>
		atom(null, async (get, set) => {
			const editorState = get($pageEditorState({ documentId, pageId }));
			if (!editorState) {
				logger.debug("Not compiling: no editor state", { documentId, pageId });
				return;
			}

			// TODO: Implement actual cell compilation
			// This will need to:
			// 1. Extract cells from ProseMirror doc
			// 2. Compile changed cells
			// 3. Update $compiledCells atom
			logger.debug("Compiling page (not yet implemented)", { documentId, pageId });

			// Placeholder: set empty compiled cells
			set($compiledCells({ documentId, pageId }), {});
		}),
	isEqual
);
